// ═════════════════════════════════════════════════════════════════════════════
// Corporate Rate Card admin API client
//
// Wraps the EMR corporate-billing endpoints (see readmd/ipd-corporate-rate-card-flow.md).
// All calls are tenant-scoped EMR wrappers under /app/api/ipd/billing/corporate-*.
// The UI never talks to billing microservices directly — EMR headers only.
//
//   Payers        GET/POST  /ipd/billing/corporate-payers/
//   Rate cards    GET/POST /ipd/billing/corporate-rate-cards/[{id}/][update/|status/]
//   Items         GET/POST .../{id}/items/[{itemId}/update/|{itemId}/delete/]
//                 POST .../{id}/items/bulk/      (manual grid save)
//                 POST .../{id}/items/import/    (CSV/Excel upload — multipart)
//                 GET  /corporate-rate-cards/import-template/
//   Preview       POST /corporate-rate-cards/preview/           (global)
//                 POST /ipd_admission/{id}/billing/corporate-pricing/preview/
// ═════════════════════════════════════════════════════════════════════════════

import axios from "axios";
import { getBillingHeaders, trimId, sanitizeErrorMessage } from "./billing-api";

const API_ROOT = process.env.REACT_APP_BACKEND_ROUTE_URL || "";
const CORP_ROOT = `${API_ROOT}ipd/billing/corporate-rate-cards`;
const PAYERS_ROOT = `${API_ROOT}ipd/billing/corporate-payers`;

// ── low-level helpers ─────────────────────────────────────────────────────────

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function unwrapObject(data) {
  if (!data || typeof data !== "object") return data;
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  return data;
}

function unwrapList(data, keys = []) {
  if (Array.isArray(data)) return data;
  for (const key of ["items", "data", "results", ...keys]) {
    const val = data?.[key];
    if (Array.isArray(val)) return val;
    if (val && typeof val === "object" && Array.isArray(val.items)) return val.items;
  }
  return [];
}

/** JSON request with the shared billing error handling. */
async function request(method, url, body) {
  try {
    const res = await axios({
      method,
      url,
      data: body,
      headers: getBillingHeaders(body != null),
    });
    if (res.data?.status === "Failed") {
      const wrapped = new Error(sanitizeErrorMessage(res.data.message || "Request failed"));
      wrapped.status = res.data.status_code || res.data.code;
      throw wrapped;
    }
    return res.data;
  } catch (err) {
    if (err.response?.status === 401) window.location.href = "/login";
    if (err && err.status !== undefined && !err.response) throw err;
    const status = err.response?.status;
    const rawMsg =
      err.response?.data?.message ||
      err.response?.data?.detail ||
      (typeof err.response?.data === "string" ? err.response.data : "") ||
      err.message ||
      "Request failed";
    const wrapped = new Error(sanitizeErrorMessage(rawMsg, status));
    wrapped.status = status;
    wrapped.code = err.response?.data?.code || err.response?.data?.error_code;
    throw wrapped;
  }
}

/**
 * Multipart upload — must NOT force application/json so axios can set the
 * multipart boundary itself. getBillingHeaders(false) omits Content-Type.
 */
async function uploadRequest(url, formData) {
  try {
    const res = await axios.post(url, formData, { headers: getBillingHeaders(false) });
    if (res.data?.status === "Failed") {
      const wrapped = new Error(sanitizeErrorMessage(res.data.message || "Upload failed"));
      wrapped.status = res.data.status_code || res.data.code;
      throw wrapped;
    }
    return res.data;
  } catch (err) {
    if (err.response?.status === 401) window.location.href = "/login";
    if (err && err.status !== undefined && !err.response) throw err;
    const status = err.response?.status;
    const rawMsg =
      err.response?.data?.message || err.response?.data?.detail || err.message || "Upload failed";
    const wrapped = new Error(sanitizeErrorMessage(rawMsg, status));
    wrapped.status = status;
    throw wrapped;
  }
}

function qs(params) {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}

// ── mappers (API → UI) ────────────────────────────────────────────────────────

export function mapPayer(p = {}) {
  return {
    id: p.id ?? p.payer_id,
    code: p.payer_code ?? p.code ?? "—",
    name: p.payer_name ?? p.name ?? "—",
    type: p.payer_type ?? "CORPORATE",
    active: p.is_active ?? p.active ?? true,
  };
}

export function mapRateCard(c = {}) {
  return {
    id: c.id ?? c.rate_card_id,
    payerId: c.payer_id ?? null,
    payerName: c.payer_name ?? c.corporate_name ?? null,
    code: c.rate_card_code ?? "—",
    name: c.rate_card_name ?? "—",
    type: c.rate_card_type ?? "MASTER",
    encounterType: c.encounter_type ?? "IPD",
    status: c.rate_card_status ?? (c.is_active ? "ACTIVE" : "INACTIVE"),
    priority: c.priority ?? null,
    effectiveFrom: c.effective_from ?? null,
    effectiveTo: c.effective_to ?? null,
    active: c.is_active ?? true,
    itemCount: c.item_count ?? c.items_count ?? null,
  };
}

export function mapRateCardItem(it = {}) {
  return {
    id: it.id ?? it.item_id,
    rateCardId: it.corporate_rate_card_id ?? null,
    scopeType: it.scope_type ?? "SERVICE_CODE",
    scopeCode: it.scope_code ?? null,
    scopeName: it.scope_name ?? null,
    bedType: it.bed_type_code ?? null,
    pricingMethod: it.pricing_method ?? "FIXED_RATE",
    standardUnitPrice: it.standard_unit_price != null ? num(it.standard_unit_price) : null,
    corporateUnitPrice: it.corporate_unit_price != null ? num(it.corporate_unit_price) : null,
    discountPercentage: it.discount_percentage != null ? num(it.discount_percentage) : null,
    discountAmount: it.discount_amount != null ? num(it.discount_amount) : null,
    priority: it.priority ?? 10,
    // Excluded = not covered by the contract. Servers that don't model the flag
    // still round-trip as a 0% discount line, which is how the editor saves it.
    excluded:
      it.is_excluded ||
      it.pricing_method === "EXCLUDE" ||
      (it.pricing_method === "PERCENTAGE_DISCOUNT" && num(it.discount_percentage) === 0),
    effectiveFrom: it.effective_from ?? null,
    effectiveTo: it.effective_to ?? null,
    active: it.is_active ?? true,
  };
}

// ── Corporate payers ──────────────────────────────────────────────────────────

/**
 * EMR-side payer master, filtered by finance class (e.g. "CORP").
 * GET /ipd_admission/payers_by_finance_class/?financial_class=CORP
 * Drives the "All corporates" picker and the "New corporate" creation list.
 */
export async function listPayersByFinanceClass(financialClass = "CORP") {
  const data = await request(
    "GET",
    `${API_ROOT}ipd_admission/payers_by_finance_class/${qs({ financial_class: financialClass })}`
  );
  // Response shape: { payers: [{ label, value }] } — `value` doubles as the code/id.
  return unwrapList(data, ["payers"]).map((p) => ({
    id: p.value ?? p.id ?? p.payer_id,
    code: p.value ?? p.code ?? p.payer_code ?? "—",
    name: p.label ?? p.name ?? p.payer_name ?? "—",
  }));
}

// The endpoint is already the corporate-payer list, so we don't send
// payer_type — the server defaults it to CORPORATE.
export async function listCorporatePayers({
  search,
  limit = 50,
  offset = 0,
  activeOnly = true,
} = {}) {
  const data = await request(
    "GET",
    `${PAYERS_ROOT}/${qs({ search, limit, offset, active_only: activeOnly })}`
  );
  return unwrapList(data, ["payers"]).map(mapPayer);
}

/** POST /corporate-payers/ → { payer_id, payer:{...} }. Returns the mapped payer (id preserved). */
export async function createCorporatePayer(body) {
  const data = await request("POST", `${PAYERS_ROOT}/`, body);
  const obj = unwrapObject(data);
  const payer = mapPayer(obj.payer || obj);
  if (!payer.id && obj.payer_id) payer.id = obj.payer_id;
  return payer;
}

// ── Rate card header ──────────────────────────────────────────────────────────

export async function listRateCards({ payerId, status, activeOnly = true } = {}) {
  const data = await request(
    "GET",
    `${CORP_ROOT}/${qs({ payer_id: payerId, status, active_only: activeOnly })}`
  );
  return unwrapList(data, ["rate_cards"]).map(mapRateCard);
}

export async function getRateCard(rateCardId) {
  const data = await request("GET", `${CORP_ROOT}/${trimId(rateCardId)}/`);
  const obj = unwrapObject(data);
  return mapRateCard(obj.rate_card || obj);
}

export async function createRateCard(body) {
  const data = await request("POST", `${CORP_ROOT}/`, body);
  const obj = unwrapObject(data);
  return mapRateCard(obj.rate_card || obj);
}

export async function updateRateCard(rateCardId, body) {
  const data = await request("POST", `${CORP_ROOT}/${trimId(rateCardId)}/update/`, body);
  const obj = unwrapObject(data);
  return mapRateCard(obj.rate_card || obj);
}

/** POST .../{id}/status/ body { rate_card_status } (platform is GET/POST only) */
export async function setRateCardStatus(rateCardId, rateCardStatus) {
  const data = await request("POST", `${CORP_ROOT}/${trimId(rateCardId)}/status/`, {
    rate_card_status: rateCardStatus,
  });
  return unwrapObject(data);
}

// ── Rate card items ───────────────────────────────────────────────────────────

export async function listRateCardItems(rateCardId, { activeOnly = true } = {}) {
  const data = await request(
    "GET",
    `${CORP_ROOT}/${trimId(rateCardId)}/items/${qs({ active_only: activeOnly })}`
  );
  return unwrapList(data, ["rate_card_items", "line_items"]).map(mapRateCardItem);
}

export async function createRateCardItem(rateCardId, body) {
  const data = await request("POST", `${CORP_ROOT}/${trimId(rateCardId)}/items/`, body);
  const obj = unwrapObject(data);
  return mapRateCardItem(obj.item || obj);
}

/**
 * Manual grid save — same validation as import.
 * body: { effective_from, dry_run, replace_existing, items: [{ service_code, pricing_method, corporate_unit_price, ... }] }
 */
export async function bulkSaveRateCardItems(rateCardId, body) {
  const data = await request("POST", `${CORP_ROOT}/${trimId(rateCardId)}/items/bulk/`, body);
  return unwrapObject(data);
}

/**
 * CSV/Excel upload (multipart). Returns the import summary
 * { total_rows, created_count, error_count, created:[], errors:[] }.
 */
export async function importRateCardItems(
  rateCardId,
  file,
  { dryRun = false, replaceExisting = false, effectiveFrom } = {}
) {
  const fd = new FormData();
  fd.append("file", file);
  const url = `${CORP_ROOT}/${trimId(rateCardId)}/items/import/${qs({
    dry_run: dryRun,
    replace_existing: replaceExisting,
    effective_from: effectiveFrom,
  })}`;
  return unwrapObject(await uploadRequest(url, fd));
}

export async function updateRateCardItem(rateCardId, itemId, body) {
  const data = await request(
    "POST",
    `${CORP_ROOT}/${trimId(rateCardId)}/items/${trimId(itemId)}/update/`,
    body
  );
  const obj = unwrapObject(data);
  return mapRateCardItem(obj.item || obj);
}

/** Soft deactivate (is_active=false) — POST .../items/{itemId}/delete/. */
export async function deleteRateCardItem(rateCardId, itemId) {
  const data = await request(
    "POST",
    `${CORP_ROOT}/${trimId(rateCardId)}/items/${trimId(itemId)}/delete/`,
    {}
  );
  return unwrapObject(data);
}

/** GET import-template/ → CSV text for the download button. */
export async function getImportTemplate() {
  const data = await request("GET", `${CORP_ROOT}/import-template/`);
  const obj = unwrapObject(data);
  return obj.csv ?? obj.template ?? (typeof obj === "string" ? obj : "");
}

// ── Preview ───────────────────────────────────────────────────────────────────

/** Global preview (admin test) — POST /corporate-rate-cards/preview/. */
export async function previewGlobalPricing(body) {
  const data = await request("POST", `${CORP_ROOT}/preview/`, body);
  const obj = unwrapObject(data);
  return obj.pricing || obj;
}

/**
 * Admission-scoped preview — POST /ipd_admission/{id}/billing/corporate-pricing/preview/.
 * `payer_id` is optional: the server reads the admission's billing_payer_id
 * (set at bed allocation's billing_setup). Returns the `pricing` object with
 * pricing_applied, standard_unit_price, corporate_unit_price, corporate_discount_amount, …
 */
export async function previewCorporatePricing(admissionId, body) {
  const url = `${API_ROOT}ipd_admission/${trimId(admissionId)}/billing/corporate-pricing/preview/`;
  const data = await request("POST", url, body);
  const obj = unwrapObject(data);
  return obj.pricing || obj;
}
